import {
  Box,
  Button,
  FormControl,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import CodeRepositoryButton from './CodeRepositoryButton';
import { DiGit } from 'react-icons/di';
import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import AdvancedOptions, { AdvancedOptionsCredTypes } from './AdvancedOptions';
import { CodeRepos } from '../onboarding-types';
import { Nullable } from 'common-types';
import { Connector } from '../../connectors/connectors-types';
import { codeRepoIconMap } from '../onboarding-utils';
import { LoadingButton } from '@mui/lab';
import { isRemoteSite } from 'env-utils';

const OnBoardingForm: FC<React.PropsWithChildren<OnBoardingFormProps>> = ({
  connecting,
  openAdvancedOptions,
  handleOpenAdvancedOptions,
  selectedConnector,
  repoError,
  onClick,
  organizationName,
  onConnect,
  connectors,
  onAdvancedOptionChange,
  hostUrlValue,
  tokenOrPasswordValue,
  onAdvanceOptionsInputChange,
  disableConnect,
  onOrgChange,
  usernameValue,
  connectTooltipMsg,
  onDemoScanClick,
  isValidOrgDisplayName,
  invalidOrgDisplayNameHelperText,
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.onBoardingForm}>
      <div className={classes.repoButtons}>
        {connectors.map(connector => {
          const RepoIcon =
            codeRepoIconMap.get(connector.name as CodeRepos) || DiGit;
          return (
            <CodeRepositoryButton
              key={connector.name}
              repoName={connector.name}
              onRepoSelect={onClick}
              selected={selectedConnector?.name === connector.name}
              error={!!repoError && repoError === connector.name}>
              <div className={classes.buttonContentWrapper}>
                <RepoIcon className={classes.repoIcon} />
                <Typography color='onboarding.codeRepoButton.text'>
                  {connector.displayName}
                </Typography>
              </div>
            </CodeRepositoryButton>
          );
        })}
      </div>
      {!isRemoteSite() && (
        <div className={classes.demoDataLinkContainer}>
          <Button variant='text' color='primary' onClick={onDemoScanClick}>
            <Typography variant='body1' textTransform='none' fontWeight='bold'>
              Alternatively try the OX Demo
            </Typography>
          </Button>
        </div>
      )}
      <div className={classes.orgName}>
        <Typography variant='body2' color='text.secondary' mt={2}>
          Organization Name
        </Typography>
        <FormControl className={classes.formControl} margin='dense'>
          <TextField
            variant='outlined'
            onChange={onOrgChange}
            placeholder='[user name] Org'
            value={organizationName}
            helperText={
              <Typography component='span' color='error'>
                {invalidOrgDisplayNameHelperText}
              </Typography>
            }
          />
        </FormControl>
      </div>

      <AdvancedOptions
        disabled={!selectedConnector}
        hostUrl={hostUrlValue}
        tokenOrPasswordValue={tokenOrPasswordValue}
        usernameValue={usernameValue}
        onInputChange={onAdvanceOptionsInputChange}
        onOptionChange={onAdvancedOptionChange}
        open={openAdvancedOptions}
        handleOpenClose={handleOpenAdvancedOptions}
        error={!!selectedConnector?.errorMessage}
        errorMessage={selectedConnector?.errorMessage}
        credentialsType={
          selectedConnector?.name !== CodeRepos.Bitbucket
            ? AdvancedOptionsCredTypes.Token
            : AdvancedOptionsCredTypes.UserAndPassword
        }
        chosenRepoDisplayName={selectedConnector?.displayName}
        chosenRepoName={selectedConnector?.name as CodeRepos}
      />
      <Box mt={2}>
        <Tooltip arrow placement='top' title={connectTooltipMsg}>
          <span className={classes.connectButtonWrapper}>
            <LoadingButton
              onClick={onConnect}
              variant='contained'
              color={connecting ? 'primary' : undefined}
              disabled={disableConnect || !isValidOrgDisplayName}
              loading={connecting}
              className={classes.connectButton}>
              Connect
            </LoadingButton>
          </span>
        </Tooltip>
      </Box>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  onBoardingForm: {
    maxWidth: 305,
    margin: '0 auto',
    backgroundColor: theme.palette.background.paper,
  },
  repoButtons: {
    marginBlockStart: theme.spacing(1.5),
    '& > button': {
      width: '100%',
      marginBlockStart: theme.spacing(1.5),
    },
  },
  orgName: {
    marginBlockStart: theme.spacing(1.5),
    color: theme.palette.text.primary,
    textAlign: 'start',
  },
  formControl: {
    width: '100%',
    marginBlockStart: theme.spacing(1),
  },
  connectButton: {
    borderRadius: `50px !important`,
    marginBlockStart: theme.spacing(2),
    minWidth: `170px !important`,
    display: 'inline-block',
  },
  connectButtonWrapper: {
    height: '100%',
    display: 'inline-block',
  },
  buttonContentWrapper: {
    display: 'flex',
    paddingInlineStart: '32%',
    alignItems: 'center',
  },
  repoIcon: {
    marginRight: theme.spacing(1.5),
    width: '30px',
    flexShrink: 0,
  },
  demoDataLinkContainer: {
    marginBlockStart: theme.spacing(2),
  },
}));

interface OnBoardingFormProps {
  connectors: Connector[];
  connecting: boolean;
  openAdvancedOptions: boolean;
  handleOpenAdvancedOptions: () => void;
  selectedConnector: Nullable<Connector>;
  repoError?: CodeRepos;
  onClick: (repoName: string) => void;
  organizationName: string;
  onConnect: () => void;
  onAdvancedOptionChange: (bool: boolean) => void;
  hostUrlValue: string;
  tokenOrPasswordValue: string;
  onAdvanceOptionsInputChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onOrgChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disableConnect: boolean;
  usernameValue: string;
  connectTooltipMsg: string;
  onDemoScanClick: () => void;
  isValidOrgDisplayName: boolean;
  invalidOrgDisplayNameHelperText: string;
}

export default OnBoardingForm;
